import get from "lodash/get";
import {useSearchParams} from "react-router";

import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {Sorting} from "@/components/table/Table.types.ts";
import {RUMTableSegment} from "@/lib/queries/rum.ts";
// @ts-expect-error legacy
import {checkFeatures} from "@/pages/rum/v2/CheckFeatures.js";
import {RUMSection} from "@/pages/rum/v2/details/graphs/segments/SectionBtnGroup.tsx";
import {URLGroupCell} from "@/pages/rum/v2/details/graphs/segments/URLGroupCell.tsx";
import {RUMBaselineComparison} from "@/pages/rum/v2/list/RUMBaselineComparison.tsx";

interface SectionSegmentsTableProps {
  className?: string;
  rows: RUMTableSegment[];
  sorting: Sorting;
  section: RUMSection;
  drilldown: string | null;
  setDrilldown: (param: string) => void;
  onSortingChange: (sorting: Sorting) => void;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
}

export const SectionSegmentsTable = ({
  className,
  rows,
  sorting,
  section,
  drilldown,
  setDrilldown,
  onSortingChange,
  pagination,
  totalRows,
  updatePagination,
}: SectionSegmentsTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {pageViews, sessions, firstByte, firstPaint, tti} = checkFeatures;

  let firstColumn;
  switch (section) {
    case RUMSection.DEVICE:
      firstColumn = {
        fieldTitle: "Device",
        fieldName: "Device",
      };
      break;
    case RUMSection.PLATFORM:
      firstColumn = {
        fieldTitle: "Platform",
        fieldName: "Platform",
      };
      break;
    case RUMSection.BROWSER:
      if (drilldown) {
        firstColumn = {
          fieldTitle: "Browser Version",
          fieldName: "BrowserVersion",
        };
      } else {
        firstColumn = {
          fieldTitle: "Browser",
          fieldName: "Browser",
          accessor: (v: unknown) => (
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => {
                if (get(v, "Browser")) {
                  setDrilldown(get(v, "Browser", ""));
                }
              }}
            >
              {get(v, "Browser")}
            </button>
          ),
          meta: {
            columnClassName: "text-break",
          },
        };
      }
      break;
    case RUMSection.COUNTRY:
      if (drilldown) {
        firstColumn = {
          fieldTitle: "Region",
          fieldName: "Region",
        };
      } else {
        firstColumn = {
          fieldTitle: "Country",
          fieldName: "CountryName",
          accessor: (v: unknown) => (
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => {
                if (get(v, "Country")) {
                  setDrilldown(get(v, "Country", ""));
                }
              }}
            >
              {get(v, "CountryName")}
            </button>
          ),
          meta: {
            columnClassName: "text-break",
          },
        };
      }
      break;
    default:
      firstColumn = {
        fieldTitle: "URL / Group",
        fieldName: "url",
        accessor: (v: unknown) => (
          <URLGroupCell
            onURLClick={(u) => {
              const params = new URLSearchParams(searchParams);
              params.set("url", u);
              setSearchParams(params);
            }}
            v={v as RUMTableSegment}
          />
        ),
        meta: {
          columnClassName: "text-break",
        },
      };
  }

  const specs = [
    firstColumn,
    {
      fieldTitle: pageViews.name,
      fieldName: "PageViews",
      enableSorting: true,
      accessor: (v: unknown) => <RUMBaselineComparison feature={pageViews} rowData={v} />,
    },
    {
      fieldTitle: sessions.name,
      fieldName: "Sessions",
      enableSorting: true,
      accessor: (v: unknown) => <RUMBaselineComparison feature={sessions} rowData={v} />,
    },
    {
      fieldTitle: firstByte.name,
      fieldName: "FirstByte",
      enableSorting: true,
      accessor: (v: unknown) => <RUMBaselineComparison feature={firstByte} rowData={v} />,
    },
    {
      fieldTitle: firstPaint.name,
      fieldName: "FirstPaint",
      enableSorting: true,
      accessor: (v: unknown) => <RUMBaselineComparison feature={firstPaint} rowData={v} />,
    },
    {
      fieldTitle: tti.name,
      fieldName: "TTI",
      enableSorting: true,
      accessor: (v: unknown) => <RUMBaselineComparison feature={tti} rowData={v} />,
    },
  ];
  return (
    <Table
      className={className}
      minWidth="800px"
      onSortingChange={onSortingChange}
      rows={rows}
      sorting={sorting}
      specs={specs}
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
    />
  );
};
